// Utils de notificacao compartilhados entre NotificationBell e AdminNotificationsPage.
// Extraidos do NotificationBell.tsx para satisfazer react-refresh/only-export-components
// (um arquivo de componente nao deve exportar tambem constantes/funcoes utilitarias).
import type { AppNotification, NotificationType } from "./types";

export const TYPE_LABEL: Record<NotificationType, string> = {
  new_sku: "Novo SKU",
  sync_failed: "Falha de sincronização",
  drive_invalid_photo: "Foto inválida",
  drive_photo_deleted: "Foto removida",
  rollback_done: "Rollback aplicado",
  generic: "Aviso",
};

// Rotulo do CHIP por notificacao: o tipo "generic" cobre varios eventos
// (conflito de variacao, cor sem pasta, promo, SKU inativado, reseed) e o
// chip "Aviso" + texto "Notificacao" nao dizia nada (print do dono 23/09).
const GENERIC_KIND_LABEL: Record<string, string> = {
  variation_conflict: "Conflito de variação",
  drive_color_unmatched: "Cor sem correspondência",
  promo_expired: "Promoção expirada",
  promo_above_base: "Promoção acima do preço",
  sku_inactivated_unpublish: "Produto desativado",
  // Mercado Livre (canal-ml F5/F2): o texto pronto vem em payload.message
  ml_item_alerta: "Anúncio do ML",
  ml_pedido_novo: "Pedido no ML",
  ml_pergunta: "Pergunta no ML",
  ml_mensagem: "Mensagem no ML",
  ml_reclamacao: "Reclamação no ML",
  ml_nota_fiscal: "Nota fiscal no ML",
  ml_pagamento: "Pagamento no ML",
  ml_reconciliacao: "Conferência do ML",
  ml_desconectado: "ML desconectado",
};

/** Notificação do Mercado Livre: texto pronto no payload (kind ml_*). */
function mlMessage(p: Record<string, unknown>): string | null {
  return typeof p.kind === "string" && p.kind.startsWith("ml_") && typeof p.message === "string" && p.message ? p.message : null;
}

/** Link seguro (https) que a notificação carrega, ex.: o pedido ou o anúncio no painel do ML. */
export function notificationLink(n: AppNotification): { href: string; label: string } | null {
  const p = n.payload ?? {};
  if (typeof p.url !== "string" || !p.url.startsWith("https://")) return null;
  return { href: p.url, label: typeof p.kind === "string" && p.kind.startsWith("ml_") ? "Abrir no Mercado Livre" : "Abrir" };
}

export function labelNotification(n: AppNotification): string {
  if (n.type !== "generic") return TYPE_LABEL[n.type];
  const p = n.payload ?? {};
  const kindLabel = typeof p.kind === "string" ? GENERIC_KIND_LABEL[p.kind] : undefined;
  if (kindLabel) return kindLabel;
  if (p.event === "reseed_taxonomy_done") return "Categorias sincronizadas";
  return TYPE_LABEL.generic;
}

export function summarizeNotification(n: AppNotification): string {
  const p = n.payload ?? {};
  switch (n.type) {
    case "new_sku":
      return `Novo SKU detectado no Sisplan: ${p.sku ?? ""} ${p.name ? ", " + p.name : ""}`;
    case "sync_failed":
      return `Sincronização falhou (${p.action ?? ""}) em ${p.sku ?? ""}: ${truncate(String(p.error ?? "erro desconhecido"), 120)}`;
    case "drive_invalid_photo":
      return `Foto inválida ignorada: ${p.file ?? "arquivo desconhecido"} (SKU ${p.sku ?? "?"})${
        p.reason ? `. Motivo: ${p.reason}` : ""
      }`;
    case "drive_photo_deleted":
      return `Foto removida do Drive em ${p.sku ?? ""}${
        p.file ? `: ${String(p.file)}` : p.folder ? ` (pasta ${String(p.folder)})` : ""
      }`;
    case "rollback_done":
      return `Rollback aplicado (${p.scope ?? ""}) em ${p.sku ?? ""}, versão ${p.version_num ?? ""}`;
    default:
      if (mlMessage(p)) return mlMessage(p) as string;
      if (p.kind === "promo_expired")
        return `Promoção expirou no ${p.sku ?? "?"} (era R$ ${p.sale_price ?? "?"}) — revise se precisa renovar`;
      if (p.kind === "promo_above_base")
        return `ALERTA: preço base do Sisplan ficou menor ou igual à promoção no ${p.sku ?? "?"} (base R$ ${p.base_price ?? "?"} × promo R$ ${p.sale_price ?? "?"})`;
      if (p.kind === "sku_inactivated_unpublish")
        return `Produto ${p.sku ?? "?"} foi desativado no Sisplan e saiu da loja automaticamente`;
      if (p.kind === "variation_conflict") {
        const pairs = Array.isArray(p.pairs) ? (p.pairs as { color?: string | null; size?: string | null }[]) : [];
        const exemplo = pairs
          .slice(0, 3)
          .map((x) => [x.color, x.size].filter(Boolean).join(" / "))
          .filter(Boolean)
          .join(", ");
        return `${p.sku ?? "?"}: ${pairs.length || "algumas"} combinação(ões) de cor e tamanho repetidas no Sisplan${
          exemplo ? ` (${exemplo})` : ""
        }; a loja recebeu só uma de cada. Revise o cadastro no Sisplan.`;
      }
      if (p.kind === "drive_color_unmatched")
        return `${p.sku ?? "?"}: a subpasta "${p.folder ?? "?"}" no Drive não corresponde a nenhuma cor ativa do produto; a foto ${
          p.file ? `"${String(p.file)}" ` : ""
        }entrou sem cor. Renomeie a pasta com o nome da cor.`;
      if (p.event === "reseed_taxonomy_done")
        return `Categorias sincronizadas com a loja ${p.channel ?? ""} (${String(p.result ?? "").replace("_categories", " categorias")})`;
      return typeof p.event === "string" ? String(p.event) : "Aviso do sistema sem detalhe";
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "..." : s;
}
